import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { SidebarBoxKey } from 'AdmissionsGuidance';
import { type LearnerProject, isCapstoneProject } from 'LearnerProjects';
import { type StreamForDashboard } from 'Lessons';
import { CourseItemBox } from 'CourseItemBox';
import { BlockLink } from 'BlockLink';
import { Text } from 'Text';
import { ProjectDirectLinkBox, CapstoneProjectBox } from 'ProjectContentBox';
import { ImportantCtaBox } from '../../ImportantCtaBox';
import { type StatusConfig } from './StatusBox.types';
import { MAX_COURSES_DISPLAYED, MAX_PROJECTS_DISPLAYED, STATUS_BOX_TEST_ID } from './StatusBox.constants';

type StatusBoxPresentationProps = {
    statusConfig: StatusConfig;
    displayedCourses: StreamForDashboard[];
    displayedProjects: LearnerProject[];
    hasMoreCourses: boolean;
    hasMoreProjects: boolean;
    showAllCourses: boolean;
    showAllProjects: boolean;
    onToggleShowAllCourses: () => void;
    onToggleShowAllProjects: () => void;
    onCoursePress: (course: StreamForDashboard) => void;
    totalCoursesCount: number;
    totalProjectsCount: number;
    publishedStreamIdsWithAllContentStored: Record<string, boolean>;
};

const renderCoursesSection = (
    statusConfig: StatusConfig,
    displayedCourses: StreamForDashboard[],
    hasMoreCourses: boolean,
    showAllCourses: boolean,
    onToggleShowAllCourses: () => void,
    onCoursePress: (course: StreamForDashboard) => void,
    totalCoursesCount: number,
    t: (key: string, options?: Record<string, unknown>) => string,
    publishedStreamIdsWithAllContentStored: Record<string, boolean>,
) => {
    if (!statusConfig.showCoursesAndProjects || displayedCourses.length === 0) {
        return null;
    }

    return (
        <div className="mt-4">
            <div className="space-y-2">
                <Text as="h3" color="black" weight="normal" className="text-sm leading-6">
                    {t('completeCoursesTitle')}
                </Text>
                {displayedCourses.map(course => (
                    <CourseItemBox
                        key={course.id}
                        courseId={course.id}
                        title={course.title}
                        src={course.image?.formats?.['100X100']?.url || ''}
                        progressInfo={null}
                        onPress={() => onCoursePress(course)}
                        padding="2.5"
                        titleSize="sm"
                        hideEndIcon
                        availableOffline={!!publishedStreamIdsWithAllContentStored[course.id]}
                    />
                ))}
                {hasMoreCourses && (
                    <div className="flex items-center justify-center">
                        <BlockLink onPress={onToggleShowAllCourses} color="slate-grey">
                            {showAllCourses
                                ? t('showLess')
                                : t('showMoreCourses', { count: totalCoursesCount - MAX_COURSES_DISPLAYED })}
                        </BlockLink>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`ms-1 text-slate-grey transition-transform ${
                                showAllCourses ? 'rotate-180' : ''
                            }`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const renderProjectsSection = (
    statusConfig: StatusConfig,
    displayedProjects: LearnerProject[],
    hasMoreProjects: boolean,
    showAllProjects: boolean,
    onToggleShowAllProjects: () => void,
    totalProjectsCount: number,
    t: (key: string, options?: Record<string, unknown>) => string,
) => {
    if (!statusConfig.showCoursesAndProjects || displayedProjects.length === 0) {
        return null;
    }

    return (
        <div className="mt-5">
            <div className="space-y-2">
                <Text as="h3" color="black" weight="normal" className="text-sm leading-6">
                    {t('submitProjectsTitle')}
                </Text>
                {displayedProjects.map(project =>
                    isCapstoneProject(project) ? (
                        <CapstoneProjectBox key={project.id} project={project} fullWidth padding="2.5" titleSize="sm" />
                    ) : (
                        <ProjectDirectLinkBox
                            key={project.id}
                            project={project}
                            fullWidth
                            padding="2.5"
                            titleSize="sm"
                        />
                    ),
                )}
                {hasMoreProjects && (
                    <div className="flex items-center justify-center">
                        <BlockLink onPress={onToggleShowAllProjects} color="slate-grey">
                            {showAllProjects
                                ? t('showLess')
                                : t('showMoreProjects', { count: totalProjectsCount - MAX_PROJECTS_DISPLAYED })}
                        </BlockLink>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`ms-1 text-slate-grey transition-transform ${
                                showAllProjects ? 'rotate-180' : ''
                            }`}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * StatusBoxPresentation - Pure presentation component that renders the status box content
 */
const StatusBoxPresentationComponent = ({
    statusConfig,
    displayedCourses,
    displayedProjects,
    hasMoreCourses,
    hasMoreProjects,
    showAllCourses,
    showAllProjects,
    onToggleShowAllCourses,
    onToggleShowAllProjects,
    onCoursePress,
    totalCoursesCount,
    totalProjectsCount,
    publishedStreamIdsWithAllContentStored,
}: StatusBoxPresentationProps) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'statusBox' });

    return (
        <ImportantCtaBox
            headerSubtitle={
                statusConfig.headerSubtitle
                    ? t(statusConfig.headerSubtitle.key, statusConfig.headerSubtitle.params)
                    : ''
            }
            headerTitle={
                statusConfig.headerTitle ? t(statusConfig.headerTitle.key, statusConfig.headerTitle.params) : ''
            }
            icon={
                statusConfig.icon === 'default' ? (
                    'default'
                ) : (
                    <FontAwesomeIcon icon={statusConfig.icon} className="text-sm" />
                )
            }
            iconCircle={statusConfig.icon !== 'default'}
            color="primary"
            boxKey={SidebarBoxKey.schedule_status}
            testId={STATUS_BOX_TEST_ID}
            ctaText={statusConfig.cta?.text ? t(statusConfig.cta.text.key, statusConfig.cta.text.params) : ''}
            onPressCta={statusConfig.cta?.onPress}
            ctaDisabled={statusConfig.cta?.disabled}
        >
            {statusConfig.message && t(statusConfig.message.key, statusConfig.message.params)}
            {renderCoursesSection(
                statusConfig,
                displayedCourses,
                hasMoreCourses,
                showAllCourses,
                onToggleShowAllCourses,
                onCoursePress,
                totalCoursesCount,
                t,
                publishedStreamIdsWithAllContentStored,
            )}
            {renderProjectsSection(
                statusConfig,
                displayedProjects,
                hasMoreProjects,
                showAllProjects,
                onToggleShowAllProjects,
                totalProjectsCount,
                t,
            )}
        </ImportantCtaBox>
    );
};

export const StatusBoxPresentation = memo(StatusBoxPresentationComponent) as typeof StatusBoxPresentationComponent;
